import { useEffect, useState } from "react";
import { CrudPanel } from "../components/CrudPanel";
import { tenantService } from "../services/tenantService";
import { masterLookupService } from "../services/masterLookupService";
import { Building2 } from "lucide-react";
import { mobileError, sanitizeMobile, MOBILE_LENGTH } from "../utils/validation";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const emptyTenant = {
  tenantName: "",
  contactFirstName: "",
  contactLastName: "",
  address: "",
  mobile: "",
  email: "",
  username: "",
  password: "",
  subscriptionType: "",
  startDate: "",
  endDate: "",
  idProof: "",
  status: "ACTIVE",
};

function calculateEndDate(startDate, subscriptionType) {
  if (!startDate || !subscriptionType) return "";
  const start = new Date(startDate);
  if (subscriptionType === "Free Trial") {
    start.setDate(start.getDate() + 14);
    return start.toISOString().slice(0, 10);
  }
  const monthsMap = { Monthly: 1, Quarterly: 3, "Half-Yearly": 6, Yearly: 12 };
  const months = monthsMap[subscriptionType];
  if (!months) return "";
  start.setMonth(start.getMonth() + months);
  start.setDate(start.getDate() - 1);
  return start.toISOString().slice(0, 10);
}

export function Tenant({ notify, setHeaderAction, tenantId, isSuperAdmin }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyTenant);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [subscriptionTypeOptions, setSubscriptionTypeOptions] = useState([]);
  const [statusTypeOptions, setStatusTypeOptions] = useState([]);
  const formFields = ["tenantName", "contactFirstName", "contactLastName", "address", "mobile", "email", "username", "password", "subscriptionType", "startDate", "endDate", "idProof", "status", "isDeleted"];

  // Determine readonly fields based on user role and subscription type
  const getReadonlyFields = () => {
    if (tenantId) {
      // Tenant login: tenantName, subscriptionType, startDate, endDate non-editable
      return ["tenantName", "subscriptionType", "startDate", "endDate"];
    } else if (isSuperAdmin) {
      // Super admin: endDate editable only if subscriptionType is "Free Trial"
      const isFreeTrial = form.subscriptionType === "Free Trial";
      return isFreeTrial ? [] : ["endDate"];
    }
    // Default: endDate readonly
    return ["endDate"];
  };

  useEffect(() => {
    masterLookupService.getByCategory("subscription_type")
      .then((data) => setSubscriptionTypeOptions(data.map((item) => item.displayName)))
      .catch(() => setSubscriptionTypeOptions(["Monthly", "Quarterly", "Half-Yearly", "Yearly", "Free Trial"]));
  }, []);

  useEffect(() => {
    masterLookupService.getByCategory("STATUS")
      .then((data) => setStatusTypeOptions(data.map((item) => item.displayName)))
      .catch(() => setStatusTypeOptions(["Active", "Inactive"]));
  }, []);

  useEffect(() => {
    if (setHeaderAction && isSuperAdmin) {
      setHeaderAction(
        <button
          className={showForm ? "secondary" : "primary"}
          onClick={() => setShowForm(!showForm)}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {showForm ? "Hide" : "Add"} Tenant
        </button>
      );
    }
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [showForm, setHeaderAction]);

  const load = async () => {
    setLoading(true);
    try {
      const result = await tenantService.list();
      setRows(result);
    } catch (err) {
      notify(err?.message || "Failed to load tenants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visibleRows = tenantId ? rows.filter((row) => row.uuid === tenantId) : rows;

  async function save(event) {
    event.preventDefault();
    if (saving) return;
    const mobileErr = mobileError(form.mobile);
    if (mobileErr) {
      setErrors({ mobile: mobileErr });
      notify(mobileErr);
      return;
    }
    setSaving(true);
    try {
      await tenantService.save(form);
      setForm(emptyTenant);
      setShowForm(false);
      setErrors({});
      notify("Tenant saved.");
      load();
    } catch (err) {
      console.error("Tenant save error:", err);
      notify(err?.message || "Failed to save tenant.");
    } finally {
      setSaving(false);
    }
  }

  function handleFieldChange(key, value) {
    if (errors[key]) {
      setErrors((current) => ({ ...current, [key]: undefined }));
    }
    if (key === "mobile" && value) {
      setForm((prev) => ({ ...prev, username: value }));
    }
    if ((key === "startDate" || key === "subscriptionType") && form.startDate && form.subscriptionType) {
      const endDate = calculateEndDate(form.startDate, form.subscriptionType);
      setForm((prev) => ({ ...prev, endDate }));
    }
  }

  async function remove(uuid) {
    if (!window.confirm("Delete this tenant?")) return;
    try {
      await tenantService.remove(uuid);
      notify("Tenant deleted.");
      load();
    } catch (err) {
      console.error("Tenant delete error:", err);
      notify(err?.message || "Failed to delete tenant.");
    }
  }

  async function restore(uuid) {
    try {
      await tenantService.restore(uuid);
      notify("Tenant restored.");
      load();
    } catch (err) {
      console.error("Tenant restore error:", err);
      notify(err?.message || "Failed to restore tenant.");
    }
  }

  return (
    <CrudPanel
      title="Tenant"
      sectionLabel={<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Building2 size={18} />Tenant Management</span>}
      header="Tenant List"
      rows={visibleRows}
      loading={loading}
      saving={saving}
      form={form}
      setForm={setForm}
      emptyForm={emptyTenant}
      save={save}
      remove={tenantId ? undefined : remove}
      fields={formFields}
      firstRowFields={["tenantName", "contactFirstName", "contactLastName"]}
      hideTableFields={["password"]}
      dateFields={["startDate", "endDate"]}
      passwordFields={["password"]}
      requiredFields={["tenantName", "contactFirstName", "contactLastName", "address", "mobile", "email", "username", ...(form.uuid ? [] : ["password"]), "subscriptionType", "startDate", "idProof"]}
      selectFields={{ subscriptionType: subscriptionTypeOptions, status: statusTypeOptions }}
      telFields={["mobile"]}
      sanitizeFields={{ mobile: sanitizeMobile }}
      readonlyFields={getReadonlyFields()}
      hideFormFields={["isDeleted"]}
      columnLabels={{ isDeleted: "Deleted" }}
      onRestore={tenantId ? undefined : restore}
      minDateFields={{ startDate: todayISO }}
      onFieldChange={handleFieldChange}
      collapsible={true}
      showForm={showForm}
      setShowForm={setShowForm}
      buttonLabel="Tenant"
    />
  );
}
